import { AppLayout } from '@/layouts/AppLayout';
import { Outlet, useParams } from 'react-router';
import { ProjectHeader } from '@/components/project/ProjectHeader';
import { ProjectTabs } from '@/components/project/ProjectTabs';
import { useQuery } from '@tanstack/react-query';
import { projectsApi } from '@/lib/api/projects';
import { Loading } from '@/components/Loading';
import { InquiryDto, OfferDto, isProjectInactiveDays } from '@paladin-monorepo/common';
import { inquiriesApi } from '@/lib/api/inquiries';
import { offersApi } from '@/lib/api/offers';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { AlertTriangle } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '@/lib/auth';
import { isInquirerAccount, isPrince2Account } from '@/lib/permissions';
import { projectGovernanceApi } from '@/lib/api/project-governance';
import { governanceQueryKey } from '@/components/project-governance/useGovernanceMutation';

export function ProjectLayout() {
  const { t } = useTranslation();
  const { projectId } = useParams<{ projectId: string }>();
  const { user } = useAuth({ middleware: 'auth', tmp: 'ProjectLayout' });

  const { data: project, isLoading: isProjectLoading } = useQuery({
    queryKey: ['project', projectId],
    enabled: !!projectId,
    queryFn: () => projectsApi.getById(projectId as string),
  });

  const { data: inquiry, isLoading: isInquiryLoading, error: inquiryError } = useQuery<InquiryDto>({
    queryKey: ['inquiry', project?.inquiryId],
    queryFn: () => inquiriesApi.getInquiry(project?.inquiryId as string),
    enabled: !!project?.inquiryId,
  });

  const { data: offer, isLoading: isOfferLoading, error: offerError } = useQuery<OfferDto>({
    queryKey: ['offer', project?.offerId],
    queryFn: () => offersApi.get(project?.offerId as string),
    enabled: !!project?.offerId,
  });

  // Supplier users follow the customer setting: the API returns 403 when PRINCE2 is off.
  const { data: governance } = useQuery({
    queryKey: governanceQueryKey(projectId as string),
    queryFn: () => projectGovernanceApi.get(projectId as string),
    enabled: !!projectId && (!isInquirerAccount(user ?? undefined) || isPrince2Account(user)),
    retry: false,
  });

  const isLoading = isProjectLoading || (!!project?.inquiryId && isInquiryLoading) || (!!project?.offerId && isOfferLoading);

  const basePath = `/projects/${projectId}/workspace`;

  return (
    <AppLayout
      breadcrumbs={[
        { href: '/projects', children: 'Projekty' },
        { href: basePath, children: inquiry?.title ?? project?.title ?? '' },
      ]}
    >
      {isLoading && <Loading />}
      {inquiryError && <p>{inquiryError.message}</p>}
      {offerError && <p>{offerError.message}</p>}
      {!isLoading && project && (
        <>
          <ProjectHeader project={project} inquiry={inquiry} offer={offer} />
          {isProjectInactiveDays(project.lastActivityAt, new Date(), 14) && (
            <Alert variant="destructive" className="mb-4">
              <AlertTriangle />
              <AlertTitle>{t('workspace.alerts.inactiveProjectTitle')}</AlertTitle>
              <AlertDescription>{t('workspace.alerts.inactiveProject')}</AlertDescription>
            </Alert>
          )}
          <ProjectTabs
            basePath={basePath}
            projectStatus={project.status}
            hasOffer={!!project.offerId}
            governed={!!governance}
          />
          <Outlet context={{ project, inquiry, offer }} />
        </>
      )}
    </AppLayout>
  );
}

ProjectLayout.displayName = 'ProjectLayout';
